import {
  DiagnosticCenter,
  DiagnosticCenterListResponse,
  ApiDiagnosticCenterListResponse,
  DiagnosticCenterQueryParams,
} from './diagnosticCenters.dto';
import { logging, API_BASE_URL } from '@/config/env';

// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class DiagnosticCentersService {
  private baseUrl = API_BASE_URL;
  private logoutCallback: (() => void) | null = null;

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
        }

        // Handle error messages
        if (errorData.message) {
          const errorMessage = Array.isArray(errorData.message)
            ? errorData.message.join(', ')
            : errorData.message;
          throw new Error(errorMessage);
        }

        if (errorData.error?.message) {
          const errorMessage = Array.isArray(errorData.error.message)
            ? errorData.error.message.join(', ')
            : errorData.error.message;
          throw new Error(errorMessage);
        }
      } catch (error) {
        if (error instanceof Error && error.message !== errorText) {
          throw error;
        }
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for session expired in successful response
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
    }

    return data;
  }

  // Diagnostic Centers Management APIs
  async getDiagnosticCenters(
    token: string,
    params: DiagnosticCenterQueryParams = {}
  ): Promise<DiagnosticCenterListResponse> {
    customLog('API Service: getDiagnosticCenters called');
    customLog('API Service: Token:', token ? 'Present' : 'Missing');
    customLog('API Service: Params:', params);

    const queryString = new URLSearchParams();

    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status.toString());
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/diagnostic-centers${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);

    const response = await this.request<ApiDiagnosticCenterListResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response.data:', response.data);

    // Handle both nested and flat response structures
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as any;

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: responseData.data || responseData || [],
      meta: responseData.meta || { total: 0, page: 1, lastPage: 1, limit: 10 },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getDiagnosticCenter(
    token: string,
    id: number
  ): Promise<DiagnosticCenter> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: DiagnosticCenter;
    }>(`/admin/diagnostic-centers/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async createDiagnosticCenter(
    token: string,
    centerData: Partial<DiagnosticCenter>
  ): Promise<{ status: boolean; message: string; data: DiagnosticCenter }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: DiagnosticCenter;
    }>('/admin/diagnostic-centers', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(centerData),
    });
    return response;
  }

  async updateDiagnosticCenter(
    token: string,
    id: number,
    centerData: Partial<DiagnosticCenter>
  ): Promise<DiagnosticCenter> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: DiagnosticCenter;
    }>(`/admin/diagnostic-centers/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(centerData),
    });
    return response.data;
  }

  async deleteDiagnosticCenter(
    token: string,
    id: number
  ): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>(`/admin/diagnostic-centers/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { message: response.message };
  }

  async toggleCenterStatus(
    token: string,
    id: number
  ): Promise<DiagnosticCenter> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: DiagnosticCenter;
    }>(`/admin/diagnostic-centers/${id}/toggle-status`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }
}

export const diagnosticCentersService = new DiagnosticCentersService();
